import compact from "lodash/compact";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { defineMessages } from "react-intl";

const NOT_READY_TO_TRANSACT = "not_ready_to_transact";

// Both groups go through `defineMessages` so `npm run extract-main` picks them up:
// formatjs only reads descriptors at a call site or inside `defineMessages`, and
// silently skips a plain const passed to `formatMessage` by reference.
const messages = defineMessages({
  generic: {
    id: "hosting.on_submit_address_form.error_message",
    defaultMessage: "Submission failed. Please refresh and try again.",
  },
  profileIncomplete: {
    id: "hosting.space_submission_error.profile_incomplete",
    defaultMessage:
      "Before you can add a space, please add the following to your Peerspace profile: {fields}. Then come back and try again.",
  },
  profileIncompleteFallback: {
    id: "hosting.space_submission_error.profile_incomplete_fallback",
    defaultMessage:
      "Before you can add a space, please finish setting up your Peerspace profile, then come back and try again.",
  },
});

// ProfileAPI reports which fields a host still owes before they are allowed to transact (see
// profileObjTransactable in ProfileAPI/models/profiles.js) and SpacesAPI forwards them verbatim.
const missingFieldMessages = defineMessages({
  "First Name": {
    id: "hosting.space_submission_error.missing_first_name",
    defaultMessage: "your first name",
  },
  "Last Name": {
    id: "hosting.space_submission_error.missing_last_name",
    defaultMessage: "your last name",
  },
  Avatar: {
    id: "hosting.space_submission_error.missing_avatar",
    defaultMessage: "a profile photo",
  },
  "Email Verification": {
    id: "hosting.space_submission_error.missing_email_verification",
    defaultMessage: "a verified email address",
  },
  "Pending Reviews to Complete": {
    id: "hosting.space_submission_error.missing_pending_reviews",
    defaultMessage: "your outstanding reviews",
  },
});

/**
 * Normalises a rejected space request body into `{ errorCode, missing }`.
 *
 * Until peerspace/SpacesAPI#288 ships, a "not ready to transact" rejection arrives as
 * `{ error1: '<raw ProfileAPI json>' }` with no error code at all, so read that shape too - hosts
 * should not have to wait on that deploy. The `error1` branch can go once it has landed.
 */
const parseSpaceSubmissionError = (response) => {
  const errorCode = get(response, "error_code");

  if (errorCode) {
    return { errorCode, missing: get(response, "missing") || [] };
  }

  const legacyBody = get(response, "error1");

  if (!legacyBody) {
    return { errorCode: null, missing: [] };
  }

  try {
    const legacyStatus = JSON.parse(legacyBody);

    return {
      errorCode:
        false === legacyStatus.ready_to_transact ? NOT_READY_TO_TRANSACT : null,
      missing: get(legacyStatus, "missing") || [],
    };
  } catch {
    return { errorCode: null, missing: [] };
  }
};

/**
 * Turns a rejected space request into a message the host can act on. Anything unrecognised - a
 * rejected billing call, an upstream 502, the pre-#288 body we cannot parse - falls back to the
 * generic message, so the form always says something instead of failing silently.
 */
const getSpaceSubmissionErrorMessage = (response, intl) => {
  const { errorCode, missing } = parseSpaceSubmissionError(response);

  if (NOT_READY_TO_TRANSACT !== errorCode) {
    return intl.formatMessage(messages.generic);
  }

  const fields = compact(
    missing.map(
      (field) =>
        missingFieldMessages[field] &&
        intl.formatMessage(missingFieldMessages[field]),
    ),
  );

  if (isEmpty(fields)) {
    return intl.formatMessage(messages.profileIncompleteFallback);
  }

  return intl.formatMessage(messages.profileIncomplete, {
    fields: fields.join(", "),
  });
};

export { getSpaceSubmissionErrorMessage, parseSpaceSubmissionError };
